// Orchestrateur : état de la partie, boucle d'animation, liaison entre moteur, services et interface.
import { STORAGE_KEYS, MAX_LEVEL, LONG_LIFE_FACTOR, INSTALL_OFFER_INTERVAL_DAYS, VIEW_SIZE, SUMMON_VICINITY, SUMMON_RETURN_RADIUS, SUMMON_HOVER_SPEED, HINT_BUBBLES_ENABLED, HINT_MESSAGE_DELAY_MS, HINT_MESSAGE_HOLD_MS, DRAG_FLING_MAX_SPEED, DRAG_INERTIA_MIN_SPEED, GYRO_BASE_PITCH_RANGE, GYRO_INPUT_DEG, GYRO_LIVE_TIMEOUT_MS } from './config/constants.js';
import { gameTheme } from './config/theme.js';
import { generateGhosts, findCenteredGhost, findCenteredKnownGhost, moveGhosts, nearestGhost, stepGhostToward, wanderNear, countRemainingTypes, countFoundTypes } from './core/world.js';
import { generateScenery, sceneryThemeForLevel, hintDirections, hintCorners } from './core/scenery.js';
import { stepPan, stepInertia, translateCamera, wrapX, getPanLimit } from './core/camera.js';
import { gyroPanDelta, gyroVerticalPosition, smoothDirection, angleDelta, isPhoneLowered } from './core/orientation.js';
import { getLevelParams, classifyResult, effectiveFamilySize, familySizeForLevel } from './core/level.js';
import { drainSecondsPerUnit, stepDrain, chargeRemainingMs, effectiveBattery } from './core/battery.js';
import { sanitizeGallery, addPortrait, knownTypes } from './core/gallery.js';
import { scoreOf, sanitizeBestScore, settleBest, formatScore } from './core/score.js';
import { UNLOCK_ORDER, FAMILY_MEMBERS } from './data/family.js';
import { createBurst, burstAction } from './core/burst.js';
import { sanitizeRewards, recordShare, spendFastCharge, spendLongLife, collectionProgress, resetShared, iosFreeFastAvailable, cleanFreeFastLevel } from './core/shareRewards.js';
import { cleanSettings } from './core/settings.js';
import { bindSettingsSecret } from './ui/settingsSecret.js';
import { resolveLanguage, createI18n } from './core/i18n.js';
import { isInstallOfferDue, needsInstallGate } from './core/install.js';
import { loadLocal, saveLocal } from './services/storage.js';
import { createAccount } from './services/account.js';
import { createAudioEngine, MAX_VOLUME_STEP, DEFAULT_VOLUME_STEP } from './services/audio.js';
import { bindHoldRepeat } from './ui/holdRepeat.js';
import { $, show, hide } from './ui/dom.js';
import { createRenderer } from './ui/renderer.js';
import { createOverlay } from './ui/overlay.js';
import { createStats } from './ui/stats.js';
import { createPhotoView } from './ui/photoView.js';
import { createGalleryView } from './ui/galleryView.js';
import { createFilmStrip } from './ui/filmStrip.js';
import { createFilmAutoScroll } from './ui/filmAutoScroll.js';
import { createDevSettingsView } from './ui/devSettings.js';
import { createSettingsDialog } from './ui/settingsDialog.js';
import { createHelpDialog } from './ui/helpDialog.js';
import { createAboutDialog } from './ui/aboutDialog.js';
import { createFinaleDialog } from './ui/finaleDialog.js';
import { createToast } from './ui/toast.js';
import { applyTranslations } from './ui/i18n-apply.js';
import { bindInput } from './ui/input.js';
import { createGyro, isGyroSupported } from './ui/gyro.js';
import { FEATURES } from './config/features.js';
import { isNativeApp, isNativeIos, haptic } from './config/native.js';
import { applyFeatureFlags } from './ui/features.js';
import { createLayout } from './ui/layout.js';
import { view } from './ui/viewState.js';
import { createInstallOffer, detectPhone, measureDevice } from './ui/install.js';
import { offerInstallOnce } from './ui/installLaunchPopup.js';
import { createInstallGate, guardLandscape } from './ui/installGate.js';
import { createShareModal } from './ui/shareModal.js';
import { createChargeInfoDialog } from './ui/chargeInfoDialog.js';
import { dismissSplash } from './ui/splash.js';

export function createGame() {
  // Réglages mémorisés : tout ce qui vient du stockage est revalidé (voir core/settings.js)
  const savedSettings = cleanSettings(loadLocal(STORAGE_KEYS.settings, null));
  // Niveau où la partie s'est arrêtée (batterie vide, page fermée) : on la reprend là, sans recommencer au niveau de départ
  const savedLevel = Math.max(1, Math.min(MAX_LEVEL, Math.floor(Number(loadLocal(STORAGE_KEYS.level, savedSettings.startLevel)) || savedSettings.startLevel)));
  // --- État -----------------------------------------------------------------
  const s = {
    ghosts: [],
    camera: { x: 0, y: 0 },
    worldSize: 0,
    scenery: null,                // décor du niveau (ville, plage, forêt…), regénéré à chaque niveau
    lastInputAt: 0,               // performance.now() de la dernière commande (aide des flèches)
    knownCount: 0,                // kamis déjà photographiés aux niveaux précédents (voir initLevel)
    familySize: familySizeForLevel(savedLevel), // types de kamis du niveau en cours (voir initLevel)
    filmShots: 0,                 // vues sur la pellicule au début du niveau
    filmLeft: 0,                  // vues restantes (la batterie : se vide à chaque photo ET avec le temps de jeu)
    shotsLeft: 0,                 // vues restantes si seules les photos comptaient (bilan win / survive)
    filmDrain: 0,                 // temps de jeu (s) déjà écoulé vers la prochaine unité de batterie perdue
    longLife: loadLocal(STORAGE_KEYS.longLife, false) === true, // batterie longue durée en service, jusqu'à la prochaine batterie vide
    gameWon: loadLocal(STORAGE_KEYS.gameWon, false) === true, // le dernier niveau est fini : on rejoue quand on veut, la galerie reste jusque-là
    chargingUntil: loadLocal(STORAGE_KEYS.chargingUntil, 0), // batterie vide : instant (ms) de fin de charge, 0 = aucune
    ghostSpeed: 0,                // vitesse de dérive des fantômes pour ce niveau (unités/s)
    heldDirections: new Set(),    // flèches maintenues (objectif en mouvement continu)
    heldSince: 0,                 // performance.now() du début de l'appui en cours (accélération)
    panVelocity: { x: 0, y: 0 },  // élan du décor après un glisser lancé (unités monde / s)
    gyroDir: null,                // dernière direction visée par le téléphone, lissée ({yaw, pitch}, degrés)
    gyroAnchor: null,             // direction à la dernière « commande » gyroscope (voir GYRO_INPUT_DEG)
    gyroOffsetY: 0,               // écart de hauteur dû aux autres commandes (glisser, clavier…) par rapport à la visée du téléphone
    gyroLastY: null,              // hauteur de la caméra au dernier relevé du gyroscope (null = pas encore calé)
    currentLevel: savedLevel,
    state: 'waiting_to_start',    // waiting_to_start | playing | finished
    isPaused: false,
    isFirstLoad: true,
    isShooting: false,            // déclencheur enfoncé (le cadre est figé, sauf en mode continu)
    isBursting: false,            // appui long : photos en rafale, le cadre reste libre
    lastResult: null,             // 'win' | 'survive' | 'lose'
    devSettings: savedSettings,   // réglages de jeu + `reducedMotion`
    iosFreeFastLevel: cleanFreeFastLevel(loadLocal(STORAGE_KEYS.iosFreeFastLevel, 0)), // version iOS : niveau où la charge rapide offerte a été utilisée
    rewards: sanitizeRewards(loadLocal(STORAGE_KEYS.shareRewards, null), FAMILY_MEMBERS.length), // charges rapides et batteries longue durée gagnées en partageant
    gallery: sanitizeGallery(loadLocal(STORAGE_KEYS.gallery, [])),
    run: { seconds: 0, kamis: 0 },  // partie en cours (#37) : secondes de jeu et nouveaux kamis photographiés, voir core/score.js
    bestScore: sanitizeBestScore(loadLocal(STORAGE_KEYS.bestScore, 0)), // meilleur score sur cet appareil
    runBestBefore: 0,             // meilleur score au début de la partie en cours (« Nouveau record » se juge contre lui)
    lastRun: null,                // partie finie : { score, best, isNewBest }, annoncée sous le message jusqu'à la suivante
    lastOverlay: null,            // { situation, action } affiché en dernier, pour le redessiner si la langue change
  };
  // Le cadre se fige pendant qu'une photo est affichée, sauf en rafale (on vise entre deux photos)
  const frameFrozen = () => s.isShooting && !s.isBursting;
  let animationId = null;
  let lastFrameTime = 0;
  let hintTimer = null;       // l'aide des flèches est aussi réévaluée hors boucle rAF (onglet inactif)

  // --- Langue -----------------------------------------------------------
  // Par défaut, la langue du navigateur si elle est supportée (français), sinon l'anglais.
  const browserLanguages = navigator.languages?.length ? navigator.languages : (navigator.language ? [navigator.language] : []);
  const i18n = createI18n(resolveLanguage(loadLocal(STORAGE_KEYS.language, null), browserLanguages));

  // --- Dépendances ----------------------------------------------------------
  const canvas = $('gameCanvas');
  const corners = Object.fromEntries([...document.querySelectorAll('.vf-corner')].map((el) => [el.dataset.corner, el]));
  const reticle = $('vfReticle');
  const shutterBtns = [...document.querySelectorAll('[data-shutter-trigger]')]; // sous le cadre + un de chaque côté (paysage)
  const playHintEl = $('playHint');
  const btnGyro = $('btnGyro');
  const gyroToast = $('gyroToast');
  const renderer = createRenderer(canvas, $('viewfinderZone'), gameTheme);
  const overlay = createOverlay({ t: i18n.t, getLanguage: i18n.getLanguage });
  const stats = createStats({ t: i18n.t });
  const photoView = createPhotoView({ getLanguage: i18n.getLanguage });
  const galleryView = createGalleryView({ t: i18n.t, getLanguage: i18n.getLanguage });
  const filmStrip = createFilmStrip({ t: i18n.t, getLanguage: i18n.getLanguage });
  createFilmAutoScroll($('filmStrip')); // paysage : si la pellicule déborde, elle défile doucement toute seule
  const account = createAccount();
  const toast = createToast();
  // Les curseurs de réglages s'appliquent à l'instant (voir applySetting) ; la composition du niveau (taille du monde,
  // vitesse des kamis, photos par batterie, niveau de départ) est reprise à la fermeture des réglages.
  const devView = createDevSettingsView({ onChange: (settings, batteryLevel, key) => applySetting(settings, batteryLevel, key) });
  const audio = createAudioEngine({
    bpm: s.devSettings.musicBpm,
    initialVolumeStep: loadLocal(STORAGE_KEYS.volume, DEFAULT_VOLUME_STEP),
    initialMusicEnabled: loadLocal(STORAGE_KEYS.musicEnabled, true),
  });
  // Réchauffe Tone.js en arrière-plan dès maintenant : sur iOS, l'audio doit être débloqué de
  // façon synchrone dans le clic sur le bouton musique, un import encore en cours à ce moment-là
  // empêcherait le son de démarrer.
  audio.preload();
  // Le son peut être coupé par le système (iPhone : fenêtre, sélecteur de langue, retour d'un autre onglet) : on relance le
  // contexte audio au retour de l'onglet et au prochain appui, au lieu de rester muet jusqu'au rechargement de la page
  document.addEventListener('visibilitychange', () => { if (!document.hidden) audio.ensureRunning(); });
  document.addEventListener('pointerdown', () => audio.ensureRunning(), true);
  // Créé tôt : utilisé à la fois pour retarder le bouton de profil (voir bindUI) et pour la
  // popup d'installation elle-même (voir start()), sur la même instance.
  const installOffer = createInstallOffer();
  // Popup d'installation espacée : au plus une proposition tous les INSTALL_OFFER_INTERVAL_DAYS
  const installOfferDue = isInstallOfferDue(
    loadLocal(STORAGE_KEYS.installOfferedAt, null), Date.now(), INSTALL_OFFER_INTERVAL_DAYS,
  );

  const draw = () => renderer.draw(s);
  const refreshGallery = () => { galleryView.render(s.gallery); filmStrip.render(s.gallery, s.familySize); };
  const refreshStats = () => stats.update({ ghosts: s.ghosts, filmLeft: s.filmLeft, filmShots: s.filmShots, currentLevel: s.currentLevel, familySize: s.familySize, longLife: s.longLife });
  // Toute commande éteint l'aide des coins et le message d'aide sur-le-champ (sans attendre la
  // prochaine frame)
  const noteInput = () => {
    s.lastInputAt = performance.now();
    refreshHints(s.lastInputAt);
    refreshHintMessage(s.lastInputAt);
  };

  // Aide : après un temps d'inactivité, les coins du cadre de visée clignotent du côté des fantômes hors
  // champ (voir hintCorners : au-dessus = les deux coins du haut, en haut à droite = le coin haut-droite…).
  // hintedDirections (le sens dominant) sert au message d'aide, qui dit de quel côté glisser.
  let hintedDirections = new Set();
  let hintedCorners = new Set();
  const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
  function refreshHints(now) {
    const idle = s.state === 'playing' && !s.isPaused && !s.isShooting
      && now - s.lastInputAt >= s.devSettings.hintDelay * 1000;
    const nextCorners = idle ? hintCorners(s.ghosts, s.camera, VIEW_SIZE, s.worldSize, view.width) : new Set();
    hintedDirections = idle ? hintDirections(s.ghosts, s.camera, VIEW_SIZE, s.worldSize, view.width) : new Set();
    if (sameSet(nextCorners, hintedCorners)) return;
    hintedCorners = nextCorners;
    for (const [corner, el] of Object.entries(corners)) el.classList.toggle('is-hint', nextCorners.has(corner));
  }

  // Réticule : clignote dès qu'un kami est cadré dans le rayon de capture, pulsation = « tire ! »
  // isLocked mémorisé (pas seulement la classe CSS) : refreshHintMessage en a besoin pour savoir
  // si « Maintiens pour photographier » fait partie des indications à montrer.
  let isLocked = false;
  // Kami déjà dans l'album cadré (#20) : il reste sur la carte (estompé, voir ui/renderer.js) mais ne se photographie plus. Le
  // message le dit dès qu'il entre dans le cercle, une fois par entrée, et le déclencheur ne prend pas de photo (voir shootStart).
  let knownInCircleId = null;
  function refreshLock() {
    isLocked = s.state === 'playing' && !s.isShooting
      && !!findCenteredGhost(s.ghosts, s.camera, s.devSettings.captureRadius, s.worldSize);
    reticle.classList.toggle('is-locked', isLocked);
    for (const btn of shutterBtns) btn.classList.toggle('is-locked', isLocked);
    const known = s.state === 'playing' && !s.isPaused && !s.isShooting && !isLocked ? knownInCircle() : null;
    if (known && known.id !== knownInCircleId) toast.show(i18n.t('viewfinder.alreadyInAlbum'));
    knownInCircleId = known ? known.id : null;
  }
  /** Le kami du cercle est-il déjà dans l'album (et aucun kami à photographier avec lui) ? */
  const knownInCircle = () => (findCenteredGhost(s.ghosts, s.camera, s.devSettings.captureRadius, s.worldSize)
    ? null : findCenteredKnownGhost(s.ghosts, s.camera, s.devSettings.captureRadius, s.worldSize));

  // Message d'aide textuel : encore plus tard que le clignotement des coins/réticule (hintDelay +
  // HINT_MESSAGE_DELAY_MS), une phrase explique quoi faire. Une seule à la fois ; s'il y a
  // plusieurs indications actives (ex. « photographier » + une flèche), elles défilent l'une après
  // l'autre en boucle plutôt que de s'afficher ensemble.
  // Le décor se déplace en le faisant glisser : l'aide dit de quel côté chercher et dans quel sens glisser
  const DRAG_HINT_KEYS = { up: 'playHint.dragUp', down: 'playHint.dragDown', left: 'playHint.dragLeft', right: 'playHint.dragRight' };
  let hintMessageKeys = [];
  let hintMessageIndex = 0;
  let hintMessageSwitchAt = 0;
  function showHintMessage() {
    if (!hintMessageKeys.length) {
      playHintEl.textContent = '';
      playHintEl.classList.remove('is-visible');
      return;
    }
    const key = hintMessageKeys[hintMessageIndex];
    playHintEl.textContent = i18n.t(key === 'shoot' ? 'viewfinder.shootHint' : DRAG_HINT_KEYS[key]);
    playHintEl.dataset.dir = key; // positionne la bulle à côté de la zone désignée (voir main.css)
    playHintEl.classList.add('is-visible');
  }
  function refreshHintMessage(now) {
    const eligible = HINT_BUBBLES_ENABLED && s.state === 'playing' && !s.isPaused && !s.isShooting
      && now - s.lastInputAt >= s.devSettings.hintDelay * 1000 + HINT_MESSAGE_DELAY_MS;
    const keys = [];
    if (eligible) {
      if (isLocked) keys.push('shoot');
      for (const dir of ['up', 'down', 'left', 'right']) if (hintedDirections.has(dir)) keys.push(dir);
    }
    const changed = keys.length !== hintMessageKeys.length || keys.some((k, i) => k !== hintMessageKeys[i]);
    if (changed) {
      hintMessageKeys = keys;
      hintMessageIndex = 0;
      hintMessageSwitchAt = now + HINT_MESSAGE_HOLD_MS;
      showHintMessage();
    } else if (keys.length && now >= hintMessageSwitchAt) {
      hintMessageIndex = (hintMessageIndex + 1) % hintMessageKeys.length;
      hintMessageSwitchAt = now + HINT_MESSAGE_HOLD_MS;
      showHintMessage();
    }
  }

  // Aide « approche » : au-delà de summonDelay secondes d'inactivité (réglage, 0 = tout de suite), le kami le plus
  // proche vient à portée de vue tout seul, au lieu d'attendre que le joueur le trouve — puis
  // continue de flâner près de la caméra (jamais figé, pas forcément dans le rayon de capture :
  // c'est le réticule, via refreshLock, qui signale quand il y est vraiment). Annulée dès la
  // moindre commande (s.lastInputAt repart alors de zéro, voir noteInput).
  let summonedGhostId = null;
  let summonArrived = false; // une fois arrivé, il flâne : il ne se fait pas rappeler en ligne droite dès qu'il dépasse le rayon
  function updateSummon(dt, now) {
    const idle = s.state === 'playing' && !s.isPaused && !s.isShooting
      && now - s.lastInputAt >= s.devSettings.summonDelay * 1000;
    if (!idle) { summonedGhostId = null; summonArrived = false; return; }
    let ghost = summonedGhostId != null ? s.ghosts.find((g) => g.id === summonedGhostId && !g.captured && !g.known) : null;
    if (!ghost) {
      ghost = nearestGhost(s.ghosts, s.camera, s.worldSize);
      summonedGhostId = ghost ? ghost.id : null;
      summonArrived = false;
    }
    if (!ghost) return;
    const dx = wrapX(ghost.x - s.camera.x, s.worldSize);
    if (Math.hypot(dx, ghost.y - s.camera.y) <= SUMMON_VICINITY) summonArrived = true;
    if (summonArrived) wanderNear(ghost, s.camera, SUMMON_RETURN_RADIUS, SUMMON_HOVER_SPEED, dt, Math.random, s.worldSize);
    else stepGhostToward(ghost, s.camera, s.devSettings.summonSpeed, SUMMON_VICINITY, dt, s.worldSize);
  }
  // Mémorise le dernier écran affiché pour pouvoir le redessiner si la langue change pendant qu'il est visible
  const showOverlay = (situation, action, messageKey = null) => {
    s.lastOverlay = { situation, action, messageKey };
    overlay.display(situation, action, messageKey);
    refreshScoreLine();
  };

  // --- Score (#37) ------------------------------------------------------------
  // Une partie va de « Jouer » (ou « Continuer » après une charge, « Rejouer ») jusqu'à la batterie vide ou la fin du dernier
  // niveau ; passer un niveau la prolonge. Le temps de jeu compte comme l'autonomie de la batterie (pas pendant une pause, une
  // fenêtre ni l'appui sur le déclencheur), et chaque nouveau kami photographié ajoute un peu (voir core/score.js).
  const runScore = () => scoreOf(s.run);
  const formatPoints = (points) => formatScore(points, i18n.getLanguage());
  /** Le score du viseur, qui monte chaque seconde. */
  const refreshScore = () => stats.updateScore(formatPoints(runScore()));

  /** Nouvelle partie : le score repart de zéro. */
  function startRun() {
    s.run = { seconds: 0, kamis: 0 };
    s.runBestBefore = s.bestScore;
    s.lastRun = null;
    refreshScore();
  }

  /** Temps de jeu : le score du viseur change à chaque seconde entière. */
  function addPlayTime(dt) {
    const before = Math.floor(s.run.seconds);
    s.run.seconds += dt;
    if (Math.floor(s.run.seconds) !== before) refreshScore();
  }

  /**
   * Le meilleur score suit la partie en cours dès qu'elle le dépasse (à chaque fin de niveau, et quand l'appli passe en arrière-plan) :
   * une partie interrompue (appli fermée, page rechargée) le garde, même si son score, lui, repart de zéro.
   */
  function saveBestScore() {
    const score = runScore();
    if (score <= s.bestScore) return;
    s.bestScore = score;
    saveLocal(STORAGE_KEYS.bestScore, score);
  }

  /** Fin de partie (batterie vide, dernier niveau fini) : son score et le record s'affichent sous le message. */
  function endRun() {
    const score = runScore();
    saveBestScore();
    s.lastRun = { score, ...settleBest(s.runBestBefore, score) };
  }

  /** Ligne de score sous le message affiché : la partie qui vient de finir, la partie en cours entre deux niveaux, ou le record. */
  function refreshScoreLine() {
    let text = '';
    if (s.lastRun) {
      const { score, best, isNewBest } = s.lastRun;
      text = isNewBest ? i18n.t('score.newBest', { score: formatPoints(score) }) : i18n.t('score.final', { score: formatPoints(score), best: formatPoints(best) });
    } else if (s.state === 'finished') {
      text = i18n.t('score.soFar', { score: formatPoints(runScore()) });
    } else if (s.bestScore > 0) {
      text = i18n.t('score.best', { best: formatPoints(s.bestScore) });
    }
    overlay.setScore(text);
  }

  // --- Galerie --------------------------------------------------------------
  function addToGallery(ghost) {
    s.gallery = addPortrait(s.gallery, { familyIndex: ghost.familyIndex, color: ghost.color, level: s.currentLevel });
    saveLocal(STORAGE_KEYS.gallery, s.gallery);
    refreshGallery();
  }

  // La galerie n'est jamais vidée par une charge de batterie, un niveau gagné ou un rechargement de page : elle ne se vide que
  // quand on lance une nouvelle partie (« Rejouer » à la fin du jeu)
  function resetGallery() {
    if (s.rewards.shared.length > 0) saveRewards(resetShared(s.rewards)); // nouvelle partie : les kamis déjà partagés ne comptent plus
    if (s.iosFreeFastLevel !== 0) { // et la charge rapide offerte (version iOS) revient à chaque niveau
      s.iosFreeFastLevel = 0;
      saveLocal(STORAGE_KEYS.iosFreeFastLevel, 0);
    }
    if (s.gallery.length === 0) return;
    s.gallery = [];
    saveLocal(STORAGE_KEYS.gallery, s.gallery);
    refreshGallery();
  }

  // --- Cycle de niveau ------------------------------------------------------
  /** @param {{ keepRun?: boolean }} [options]  keepRun : niveau suivant, la partie (et son score) continue ; sinon une nouvelle commence */
  function initLevel({ keepRun = false } = {}) {
    saveLocal(STORAGE_KEYS.level, s.currentLevel); // la partie reprend à ce niveau
    if (!keepRun) startRun();
    const familySize = effectiveFamilySize({ level: s.currentLevel, setting: s.devSettings.familySize });
    const params = getLevelParams(s.currentLevel, { ...s.devSettings, familySize, photoCostPercent: batterySettings().photoCostPercent });
    renderer.resize();

    // Les kamis déjà photographiés (niveaux passés, ou ce niveau avant une batterie vide) restent sur la carte — ils compliquent
    // la traque — mais ne sont plus à trouver ; si la famille est entièrement connue (réglage de test d'une petite famille),
    // on repart de zéro pour que le niveau ait des cibles
    const familyTypes = new Set(UNLOCK_ORDER.slice(0, params.familySize));
    const earlier = new Set([...knownTypes(s.gallery)].filter((type) => familyTypes.has(type)));
    const known = earlier.size < params.familySize ? earlier : new Set();
    s.knownCount = known.size;

    s.worldSize = params.worldSize;
    s.scenery = generateScenery({ worldSize: params.worldSize, theme: sceneryThemeForLevel(s.currentLevel) });
    s.ghosts = generateGhosts({
      count: params.ghostCount,
      worldSize: params.worldSize,
      minSpacing: params.minSpacing,
      colors: gameTheme.ghostColors,
      familySize: params.familySize,
      known,
    });
    s.familySize = params.familySize; // pris au début du niveau : la cible ne bouge pas en cours de partie
    refreshGallery(); // la pellicule a un emplacement par kami de la famille
    s.camera = { x: 0, y: 0 };
    s.ghostSpeed = params.ghostSpeed;
    s.heldDirections.clear();
    s.panVelocity = { x: 0, y: 0 };
    s.filmShots = params.filmShots;
    s.filmLeft = params.filmShots;
    s.shotsLeft = params.filmShots;
    s.filmDrain = 0;
    burst.stop();
    s.isShooting = false;
    summonedGhostId = null;
    noteInput();
    photoView.hidePortrait();
    refreshStats();
    refreshHints(performance.now());

    if (s.isFirstLoad) {
      s.state = 'waiting_to_start';
      draw(); // l'overlay "start" est affiché après validation du profil
    } else {
      startPlaying();
    }
  }

  function startPlaying() {
    s.state = 'playing';
    s.gyroLastY = null; // à l'entrée en jeu, la hauteur se cale sur l'inclinaison réelle du téléphone (voir le gyroscope)
    autoStartGyro();
    noteInput();
    overlay.close();
    startAnimation();
    // La boucle rAF est suspendue quand l'onglet est caché : ce minuteur garantit que les flèches
    // reflètent l'état dès le retour, et que l'aide se déclenche même sans nouvelle frame.
    clearInterval(hintTimer);
    hintTimer = setInterval(() => refreshHints(performance.now()), 500);
    checkLevelEnd(); // batterie réglée à 0 dans les réglages dev : la charge commence dès l'entrée en jeu
  }

  // Une seule boucle rAF : objectif en mouvement continu (flèches maintenues), dérive des
  // fantômes, puis rendu
  function startAnimation() {
    stopAnimation();
    lastFrameTime = performance.now();
    const loop = (now) => {
      const dt = (now - lastFrameTime) / 1000;
      lastFrameTime = now;
      if (!s.isPaused) {
        // Pas de déplacement pendant qu'une photo est affichée
        if (s.heldDirections.size > 0 && !frameFrozen()) {
          s.camera = stepPan(s.camera, s.heldDirections, (now - s.heldSince) / 1000, dt, s.devSettings, s.worldSize);
        }
        // Élan d'un glisser lancé : le décor continue sur sa lancée et ralentit
        if (s.panVelocity.x || s.panVelocity.y) {
          const step = stepInertia(s.panVelocity, dt, s.devSettings.dragFriction, DRAG_INERTIA_MIN_SPEED);
          s.panVelocity = step.velocity;
          s.camera = translateCamera(s.camera, step.dx, step.dy, s.worldSize);
        }
        // La batterie se vide aussi avec le temps de jeu, même sans photo (pas pendant une pause, une modale ni
        // l'affichage d'une photo ; un onglet resté en arrière-plan ne compte pas non plus : dt est plafonné)
        if (s.state === 'playing' && !s.isShooting) {
          const played = Math.min(dt, 0.25);
          addPlayTime(played); // avant la batterie : la dernière fraction compte, même si elle vide la batterie
          drainBattery(played);
        }
        // Filet de sécurité : une batterie vide ne laisse jamais la partie « en cours » (le verdict a normalement déjà été rendu au dernier tir ou au dernier cran du temps)
        if (s.state === 'playing' && !s.isShooting && s.filmLeft <= 0) checkLevelEnd();
        // Le kami en cours d'approche (s'il y en a un) est écarté de la dérive normale : c'est
        // updateSummon qui le dirige, en ligne droite vers le viseur.
        moveGhosts(s.ghosts, dt, s.ghostSpeed, s.worldSize, undefined, summonedGhostId, s.camera);
        updateSummon(dt, now);
      }
      refreshHints(now);
      refreshLock();
      refreshHintMessage(now);
      draw();
      animationId = requestAnimationFrame(loop);
    };
    animationId = requestAnimationFrame(loop);
  }

  function stopAnimation() {
    if (animationId) cancelAnimationFrame(animationId);
    animationId = null;
  }

  // Idempotent : ne fait rien si le niveau n'est plus en cours
  function endLevel(situation) {
    if (s.state !== 'playing') return;
    s.state = 'finished';
    s.lastResult = situation;
    stopAnimation();
    clearInterval(hintTimer);
    hintTimer = null;
    refreshHints(0);
    refreshLock();
    refreshHintMessage(0);
    // Batterie vide ou dernier niveau fini : la partie s'arrête et son score s'affiche ; sinon elle continue au niveau suivant
    if (situation === 'lose' || s.currentLevel >= MAX_LEVEL) endRun();
    else saveBestScore();

    if (situation === 'lose') {
      audio.playLose();
      startCharging();
    } else if (situation === 'win') {
      audio.playWin();
      if (s.currentLevel >= MAX_LEVEL) showFinale();
      else showOverlay('win', 'next');
    } else {
      audio.playSurvive();
      if (s.currentLevel >= MAX_LEVEL) showFinale();
      else showOverlay('survive', 'next');
    }
    draw(); // dernière image figée
  }

  // Fin du dernier niveau : une fenêtre « Continuer » annonce la batterie longue durée gagnée (jamais de crédits offerts), puis un
  // bouton « Rejouer » dans le cadre photo, actif tout de suite : on rejoue quand on veut, et tant qu'on ne rejoue pas la
  // galerie reste. L'état « jeu gagné » survit à un rechargement de la page.
  function showFinale() {
    s.gameWon = true;
    saveLocal(STORAGE_KEYS.gameWon, true);
    // Récompense : une batterie longue durée, qui sert dès la prochaine partie (jusqu'à sa première batterie vide)
    s.longLife = true;
    saveLocal(STORAGE_KEYS.longLife, true);
    refreshStats();
    s.lastOverlay = { situation: 'finale', action: 'replay' };
    overlay.showFinale(); // bouton « Rejouer » dans le cadre photo : rien d'autre n'est bloqué
    refreshScoreLine();
    finaleDialog.open(); // la fenêtre « Continuer » qui annonce le gain
  }

  /** « Rejouer » : une nouvelle partie depuis le niveau de départ, galerie vidée (c'est ici seulement qu'elle l'est). */
  function startNewGame() {
    s.gameWon = false;
    saveLocal(STORAGE_KEYS.gameWon, false);
    clearCharge();
    resetGallery();
    s.currentLevel = s.devSettings.startLevel;
    initLevel();
  }

  // --- Batterie ---------------------------------------------------------------
  /** Le temps de jeu vide la batterie d'une unité tous les (autonomie / nombre de vues) secondes. */
  function drainBattery(dt) {
    const step = stepDrain(s.filmDrain, dt, drainSecondsPerUnit(batterySettings().batterySeconds, s.filmShots));
    s.filmDrain = step.carry;
    if (step.units <= 0) return;
    s.filmLeft = Math.max(0, s.filmLeft - step.units);
    refreshStats();
    checkLevelEnd(); // batterie vide : niveau perdu, la charge commence
  }

  /** Règle la charge actuelle de la batterie (réglages dev, en %) : 0 la vide aussitôt (la charge commence). */
  function applyBatteryLevel(percent) {
    if (!(percent < 100)) return; // pleine : rien à faire
    s.filmLeft = Math.round((s.filmShots * Math.max(0, percent)) / 100);
    refreshStats();
    checkLevelEnd();
  }

  /** Millisecondes de charge restantes. */
  const chargeRemaining = () => chargeRemainingMs(s.chargingUntil, Date.now());

  /** Charge en cours (la page a pu être rechargée entre-temps : l'instant de fin est mémorisé) ? */
  const isCharging = () => chargeRemaining() > 0;

  /** Version iOS (pas encore disponible, simulée par un réglage) : une charge rapide est offerte à chaque niveau, une seule fois. */
  // L'appli native iOS (Capacitor) est toujours en édition iOS ; sur le web, c'est le réglage de simulation.
  const freeFastReady = () => iosFreeFastAvailable(s.devSettings.iosVersion || isNativeIos(), s.currentLevel, s.iosFreeFastLevel);

  /** Autonomie et coût d'une photo en vigueur : ceux des réglages, ou ceux de la batterie longue durée. */
  const batterySettings = () => effectiveBattery(s.devSettings, s.longLife, LONG_LIFE_FACTOR);

  /** Efface la charge en cours : on peut rejouer (bouton « Recommencer »), ou le dev relance le jeu. */
  function clearCharge() {
    s.chargingUntil = 0;
    saveLocal(STORAGE_KEYS.chargingUntil, 0);
  }

  /**
   * Écran « En charge : encore X min » : pas de bouton tant que la charge dure. `action` : ce qui se propose
   * ensuite, 'restart' (batterie vide en cours de partie) ou 'start' (page rechargée pendant la charge).
   */
  function showCharging(action = 'restart') {
    s.lastOverlay = { situation: 'charging', action };
    overlay.showCharging(chargeRemaining, action, {
      longLifeRights: () => s.rewards.longLifeRights,
      longLifeActive: () => s.longLife, // déjà en service : plus la peine de la proposer
      total: () => knownTypes(s.gallery).size, // la collection : les kamis de la galerie
    });
    refreshScoreLine();
  }

  function saveRewards(next) {
    s.rewards = next;
    saveLocal(STORAGE_KEYS.shareRewards, next);
  }

  /**
   * Pas encore disponible : une fenêtre explique quoi partager (charge rapide : la carte d'un kami ; batterie longue durée : toutes celles de la
   * galerie, avec la progression), avec un bouton « Partager » qui ouvre la carte à partager.
   * @param {'fast'|'longLife'} kind
   */
  async function explainReward(kind) {
    const progress = collectionProgress(s.rewards, [...knownTypes(s.gallery)], FAMILY_MEMBERS.length);
    const choice = await paused(() => chargeInfoDialog.open({ kind, shared: progress.shared, total: progress.total }));
    if (choice === 'share') await paused(() => shareModal.open());
  }

  /**
   * Charge rapide : la charge se termine tout de suite (le bouton pour repartir apparaît). Elle vient d'abord de celle offerte au niveau
   * (version iOS), sinon d'une charge gagnée en partageant la carte d'un kami ; sans aucune, une fenêtre explique comment en gagner une.
   */
  function useFastCharge() {
    if (!isCharging()) return;
    if (freeFastReady()) {
      s.iosFreeFastLevel = s.currentLevel;
      saveLocal(STORAGE_KEYS.iosFreeFastLevel, s.iosFreeFastLevel);
    } else {
      const next = spendFastCharge(s.rewards);
      if (!next) { explainReward('fast'); return; }
      saveRewards(next);
    }
    s.chargingUntil = Date.now();
    saveLocal(STORAGE_KEYS.chargingUntil, s.chargingUntil);
    toast.show(i18n.t('overlay.fastChargeDone'));
    showCharging(s.lastOverlay?.action ?? 'restart'); // redessine : « Batterie chargée » et le bouton pour repartir
  }

  /**
   * Batterie longue durée (gagnée en partageant tous les kamis) : termine la charge tout de suite et double l'autonomie et le nombre de
   * photos (LONG_LIFE_FACTOR) jusqu'à la prochaine batterie vide.
   */
  function useLongLife() {
    if (!isCharging()) return;
    const next = spendLongLife(s.rewards);
    if (!next) { explainReward('longLife'); return; }
    saveRewards(next);
    s.longLife = true;
    saveLocal(STORAGE_KEYS.longLife, true);
    refreshStats(); // la batterie du viseur change de teinte
    s.chargingUntil = Date.now();
    saveLocal(STORAGE_KEYS.chargingUntil, s.chargingUntil);
    if (s.state === 'waiting_to_start') initLevel(); // page rechargée en pleine charge : le niveau prêt à jouer reprend la nouvelle batterie
    toast.show(i18n.t('overlay.longLifeDone'));
    showCharging(s.lastOverlay?.action ?? 'restart'); // redessine : « Batterie chargée » et le bouton pour repartir
  }

  /** Batterie vide : l'appareil se recharge pendant chargeMinutes (réglage dev, 1 par défaut), puis on recommence. */
  function startCharging() {
    s.longLife = false; // la batterie longue durée est épuisée : à regagner si on veut
    saveLocal(STORAGE_KEYS.longLife, false);
    const ms = s.devSettings.chargeMinutes * 60 * 1000;
    if (ms <= 0) {
      showOverlay('lose', 'restart'); // charge désactivée : on peut recommencer tout de suite
      return;
    }
    s.chargingUntil = Date.now() + ms;
    saveLocal(STORAGE_KEYS.chargingUntil, s.chargingUntil);
    showCharging('restart');
  }

  /** Verdict après chaque photo relâchée : les 15 types trouvés ou pellicule vide terminent le
   * niveau — jamais besoin d'épuiser les doublons des niveaux supérieurs (voir core/level.js). */
  function checkLevelEnd() {
    if (s.state !== 'playing') return;
    const result = classifyResult({
      remainingTypes: countRemainingTypes(s.ghosts, s.familySize),
      filmLeft: s.filmLeft,
      shotsLeft: s.shotsLeft,
      filmShots: s.filmShots,
      familyCount: s.familySize - s.knownCount, // les cibles du niveau : les kamis pas encore connus
    });
    if (result) endLevel(result);
  }

  // --- Commandes ------------------------------------------------------------
  /**
   * Flèche enfoncée : l'objectif part dans cette direction et accélère tant qu'on maintient
   * (intégré dans la boucle d'animation, voir startAnimation). La carte défile en sens inverse.
   */
  function panStart(direction) {
    noteInput();
    if (s.heldDirections.size === 0) s.heldSince = performance.now(); // l'accélération repart de zéro
    s.heldDirections.add(direction);
  }

  function panEnd(direction) {
    noteInput();
    s.heldDirections.delete(direction);
  }

  /** Unités monde par pixel CSS du viseur affiché. */
  // La hauteur du cadre vaut toujours VIEW_SIZE unités (le cadre est carré en portrait, plus large en paysage)
  const worldPerPixel = () => VIEW_SIZE / ($('viewfinderZone').clientHeight || VIEW_SIZE);

  /** Trackpad / molette : l'objectif suit le geste, à l'échelle du viseur (pixels -> unités monde). */
  function panByPixels(dxPx, dyPx) {
    noteInput();
    if (frameFrozen()) return;
    const k = s.devSettings.wheelSensitivity * worldPerPixel();
    s.camera = translateCamera(s.camera, dxPx * k, dyPx * k, s.worldSize);
  }

  // --- Gyroscope ------------------------------------------------------------
  // Horizontalement, déplacement relatif (chaque mesure décale la caméra de l'écart avec la précédente :
  // pas de cap à calibrer). Verticalement, position ABSOLUE : téléphone à l'horizontale (dos vers l'horizon
  // réel), l'horizon du décor est au centre du viseur ; viser plus haut monte vers le ciel. Ce que
  // les autres commandes (glisser, clavier) ajoutent à la hauteur est conservé (gyroOffsetY). Hors jeu
  // (modale, photo affichée, fin de niveau), on suit le téléphone sans bouger la caméra, pour ne pas
  // sauter au retour.
  const gyro = createGyro({
    onDirection(raw) {
      const prev = s.gyroDir;
      const dir = smoothDirection(prev, raw, s.devSettings.gyroSmoothing / 100);
      s.gyroDir = dir;
      // Téléphone baissé à -25° : on quitte le gyroscope pour le mode normal (glisser). Le choix mémorisé n'est pas modifié :
      // le bouton VR le rallume, et la prochaine partie repart comme d'habitude.
      if (s.state === 'playing' && !s.isPaused && isPhoneLowered(dir.pitch)) {
        disableGyro();
        showGyroToast('gyro.lowered');
        return;
      }
      const limit = getPanLimit(s.worldSize);
      const opts = {
        worldSize: s.worldSize,
        panLimit: limit,
        pitchRange: GYRO_BASE_PITCH_RANGE / Math.max(0.1, s.devSettings.gyroPitchSpeed),
        yawSpeed: s.devSettings.gyroYawSpeed,
      };
      // gyroHorizonOffset : l'inclinaison du téléphone qui met l'horizon du décor au centre du viseur
      const base = gyroVerticalPosition(dir.pitch - s.devSettings.gyroHorizonOffset, opts);
      if (s.state !== 'playing' || s.isPaused || frameFrozen()) {
        s.gyroOffsetY = s.camera.y - base;
        s.gyroLastY = s.camera.y;
        return;
      }
      const dx = prev ? gyroPanDelta(prev, dir, opts).dx : 0;
      if (s.gyroLastY === null) s.gyroOffsetY = 0; // première mesure : l'horizon du décor suit l'horizon réel
      else s.gyroOffsetY += s.camera.y - s.gyroLastY; // glisser, clavier… ont bougé la caméra depuis
      const y = Math.max(-limit, Math.min(limit, base + s.gyroOffsetY));
      s.camera = translateCamera(s.camera, dx, y - s.camera.y, s.worldSize);
      s.gyroLastY = s.camera.y;
      // Seul un vrai mouvement compte comme une commande (pas le tremblement de la main)
      const anchor = s.gyroAnchor ?? dir;
      if (Math.abs(angleDelta(anchor.yaw, dir.yaw)) + Math.abs(dir.pitch - anchor.pitch) >= GYRO_INPUT_DEG) {
        s.gyroAnchor = dir;
        noteInput();
      } else if (!s.gyroAnchor) {
        s.gyroAnchor = dir;
      }
    },
    onLiveChange: (live) => btnGyro.classList.toggle('is-live', live),
  });

  let gyroToastTimer = null;
  function showGyroToast(key) {
    clearTimeout(gyroToastTimer);
    gyroToast.textContent = i18n.t(key);
    gyroToast.classList.add('is-visible');
    gyroToastTimer = setTimeout(() => gyroToast.classList.remove('is-visible'), 3000);
  }

  function setGyroButton(on) {
    btnGyro.classList.toggle('is-on', on);
    btnGyro.setAttribute('aria-pressed', String(on));
  }

  /** Active la visée au gyroscope (depuis un geste utilisateur : requis sur iPhone). */
  async function enableGyro({ quiet = false } = {}) {
    if (!FEATURES.vr) return false; // mise de côté en version 1
    s.gyroDir = null; // nouvelle référence pour le cap
    s.gyroAnchor = null;
    s.gyroLastY = null; // la hauteur se cale sur l'inclinaison du téléphone à la première mesure
    const result = await gyro.start();
    if (result !== 'on') {
      setGyroButton(false);
      if (!quiet) showGyroToast(result === 'denied' ? 'gyro.denied' : 'gyro.unavailable');
      return false;
    }
    setGyroButton(true);
    // Aucune mesure réelle peu après : pas de capteur (ou bloqué) — on se rabat sur le glisser
    setTimeout(() => {
      if (!gyro.isListening() || gyro.isLive()) return;
      gyro.stop();
      setGyroButton(false);
      if (!quiet) showGyroToast('gyro.unavailable');
    }, GYRO_LIVE_TIMEOUT_MS);
    return true;
  }

  /** Au lancement d'une partie, si le joueur a activé la visée au gyroscope (case de la fenêtre de compte, ou bouton VR). */
  function autoStartGyro() {
    if (gyroAvailable && loadLocal(STORAGE_KEYS.gyroEnabled, false) && !gyro.isListening()) enableGyro({ quiet: true });
  }

  /** Bouton VR du viseur et entrée du menu du compte : coupe la réalité virtuelle, ou la rallume (autorisation redemandée). */
  async function toggleVr() {
    if (gyro.isListening()) {
      disableGyro();
      saveLocal(STORAGE_KEYS.gyroEnabled, false);
    } else if (await enableGyro()) {
      saveLocal(STORAGE_KEYS.gyroEnabled, true);
    }
  }

  function disableGyro() {
    gyro.stop();
    setGyroButton(false);
  }

  /** Doigt posé sur le viseur : il rattrape le décor, l'élan en cours s'arrête net. */
  function dragStart() {
    noteInput();
    s.panVelocity = { x: 0, y: 0 };
  }

  /** Glisser : le décor suit le doigt au pixel près, donc l'objectif part en sens inverse. */
  function dragMove(dxPx, dyPx) {
    noteInput();
    if (frameFrozen()) return;
    const k = worldPerPixel();
    s.camera = translateCamera(s.camera, -dxPx * k, -dyPx * k, s.worldSize);
  }

  /** Doigt levé : le décor garde l'élan du geste (plafonné), freiné ensuite dans la boucle. */
  function dragEnd(vxPx, vyPx) {
    noteInput();
    if (frameFrozen()) return;
    const k = worldPerPixel();
    let vx = -vxPx * k;
    let vy = -vyPx * k;
    const speed = Math.hypot(vx, vy);
    if (speed > DRAG_FLING_MAX_SPEED) {
      vx *= DRAG_FLING_MAX_SPEED / speed;
      vy *= DRAG_FLING_MAX_SPEED / speed;
    }
    s.panVelocity = { x: vx, y: vy };
  }

  /** Une photo : consomme une vue ; fantôme cadré = portrait (et capture), sinon flash raté. */
  function takePhoto() {
    s.filmLeft--;
    s.shotsLeft--;
    const ghost = findCenteredGhost(s.ghosts, s.camera, s.devSettings.captureRadius, s.worldSize);
    photoView.flash(); // le flash part à chaque photo, réussie ou non
    haptic(ghost ? 'capture' : 'shutter'); // dans les applis : le déclencheur se sent, et plus fort quand un kami est pris (#5)
    if (ghost) {
      const foundBefore = countFoundTypes(s.ghosts);
      ghost.captured = true;
      if (countFoundTypes(s.ghosts) > foundBefore) { // un nouveau kami (pas un double d'un kami déjà pris à ce niveau) : il compte au score
        s.run.kamis += 1;
        refreshScore();
      }
      audio.playCapture();
      photoView.showPortrait(ghost);
      addToGallery(ghost);
    } else {
      audio.playShutter();
    }
    refreshStats();
  }

  // Appui long : mode continu, une photo toutes les BURST_INTERVAL_MS tant que le déclencheur est tenu (voir core/burst.js)
  const burst = createBurst({
    onStart: () => { s.isBursting = true; }, // le cadre se libère : on peut viser entre deux photos
    onStop: () => { s.isBursting = false; },
    shoot: () => {
      const action = burstAction(s);
      if (action === 'finish') checkLevelEnd(); // la dernière vue est partie au premier appui : « Batterie vide » s'affiche sans attendre le relâchement
      if (action !== 'shoot') return false;
      if (knownInCircle()) return true; // kami déjà dans l'album dans le cercle : pas de photo, la rafale attend le suivant
      takePhoto();
      checkLevelEnd(); // tous les kamis trouvés, ou plus de pellicule : le niveau se termine sans attendre le relâchement
      return s.state === 'playing' && s.filmLeft > 0;
    },
  });

  /** Appui posé : une photo ; si l'appui dure, la rafale prend le relais (mode continu). Sur un kami déjà dans l'album, pas de photo
   * (ni batterie dépensée) : le message rappelle de chercher les nouveaux (#20). */
  function shootStart() {
    noteInput();
    if (s.isShooting || s.filmLeft <= 0) return;
    if (knownInCircle()) {
      toast.show(i18n.t('viewfinder.alreadyInAlbum'));
      return;
    }
    s.isShooting = true;
    s.panVelocity = { x: 0, y: 0 }; // le cadre se fige au déclenchement
    takePhoto();
    burst.arm();
  }

  /** Appui relâché : la rafale s'arrête ; le portrait de succès continue son apparition furtive tout seul (voir
   * ui/photoView.js), inutile de le couper ici ; seul le verdict de fin de niveau reste à faire. */
  function shootEnd() {
    if (!s.isShooting) return;
    burst.stop();
    noteInput();
    s.isShooting = false;
    checkLevelEnd();
  }

  // --- Pause (modals) -------------------------------------------------------
  function pause() {
    burst.stop(); // une modale s'ouvre : plus de photos
    s.isPaused = true;
    s.heldDirections.clear(); // une modale s'ouvre : l'objectif s'arrête
    s.panVelocity = { x: 0, y: 0 };
    audio.pause();
  }
  function resume() {
    s.isPaused = false;
    audio.resume();
  }

  // --- Liaison interface ----------------------------------------------------
  // Bouton gyroscope : seulement sur écran tactile, et si le navigateur peut fournir l'orientation
  // (HTTPS). Activé par défaut ; le choix du joueur est retenu.
  // VR mise de côté en version 1 (config/features.js) : tant que le drapeau est faux, le gyroscope n'est jamais disponible ni activé (le moteur reste)
  const gyroAvailable = FEATURES.vr && isGyroSupported() && !!window.matchMedia?.('(pointer: coarse)').matches;

  function bindUI() {
    // Masque l'interface des fonctionnalités mises de côté (VR), avant de lier les fenêtres ; et, dans l'appli native, ce qui ne sert que sur le web (réglage « Version iOS (simulation) »)
    applyFeatureFlags(document, { ...FEATURES, webOnly: !isNativeApp() });
    // Toujours affiché (repère de l'appareil) ; sans écran tactile ou sans HTTPS, il reste inactif
    btnGyro.disabled = !gyroAvailable;
    btnGyro.setAttribute('aria-disabled', String(!gyroAvailable));
    if (!gyroAvailable) btnGyro.title = i18n.t('gyro.unavailable');
    btnGyro.addEventListener('click', toggleVr);

    bindInput({ zone: $('viewfinderZone'), shutter: shutterBtns }, {
      onPanStart: panStart,
      onPanEnd: panEnd,
      onWheelPan: panByPixels,
      onDragStart: dragStart,
      onDragMove: dragMove,
      onDragEnd: dragEnd,
      onShootStart: shootStart,
      onShootEnd: shootEnd,
      isActive: () => s.state === 'playing' && !s.isPaused,
    });

    // Taille du viseur ajustée à l'espace laissé par les autres blocs (et au redimensionnement). Le bouton Partager est sur la ligne du
    // logo (hors du flux) : il ne compte pas.
    createLayout({
      area: $('gameArea'),
      fixed: [$('gameHeader'), $('filmStrip'), $('gameFooterButtons')],
      viewfinder: $('viewfinderZone'),
      sameWidth: [$('filmStrip')], // la pellicule va jusqu'aux mêmes bords que le cadre photo
      optional: [$('sb-level')], // le niveau s'efface sur les petits écrans plutôt que de rétrécir le viseur
      canHide: () => document.documentElement.getAttribute('data-layout') !== 'landscape', // en paysage, il tient dans le bandeau
      onChange: () => {
        if (s.ghosts.length === 0) return;
        if (renderer.resize()) draw();
      },
    });

    // En paysage le cadre prend tout l'écran (CSS) : le canvas suit sa taille réelle à chaque rotation ou redimensionnement
    new ResizeObserver(() => {
      if (renderer.resize() && s.ghosts.length > 0) draw();
    }).observe($('viewfinderZone'));

    bindDialogs();

    // Boutons de l'overlay central
    // Départ et redémarrage : depuis l'écran central, ou depuis le cadre photo une fois la batterie chargée
    const startGame = async () => {
      clearCharge();
      s.isFirstLoad = false;
      autoStartGyro(); // avant toute attente : l'autorisation iPhone exige d'être dans le geste
      await audio.init();
      startPlaying();
    };
    const restartGame = () => {
      clearCharge();
      // Batterie rechargée : on continue la partie, au même niveau, avec la galerie et les kamis déjà trouvés
      initLevel();
    };
    overlay.buttons.start.addEventListener('click', startGame);
    overlay.fastChargeButton.addEventListener('click', useFastCharge);
    overlay.longLifeButton.addEventListener('click', useLongLife);
    overlay.chargeButton.addEventListener('click', async () => {
      const action = overlay.chargeButton.dataset.action;
      if (action === 'replay') {
        startNewGame();
        if (s.state === 'waiting_to_start') await startGame(); // page rechargée sur l'écran de fin : le premier départ
      } else if (action === 'start') startGame();
      else restartGame();
    });
    overlay.buttons.next.addEventListener('click', () => {
      s.currentLevel++;
      initLevel({ keepRun: true }); // la partie continue, avec une batterie pleine
    });
    overlay.buttons.restart.addEventListener('click', restartGame);

    // Musique de fond on/off : indépendante du volume et des effets (voir services/audio.js).
    const btnMusicToggle = $('btnMusicToggle');
    const refreshMusicButton = () => {
      const on = audio.getMusicEnabled();
      btnMusicToggle.setAttribute('aria-pressed', String(on));
      btnMusicToggle.classList.toggle('is-on', on);
    };
    btnMusicToggle.addEventListener('click', () => {
      const enabled = !audio.getMusicEnabled();
      audio.setMusicEnabled(enabled);
      saveLocal(STORAGE_KEYS.musicEnabled, enabled);
      refreshMusicButton();
    });
    refreshMusicButton();

    // Volume : « Son - » et « Son + », un cran par appui ; un appui long répète et s'arrête au bout de la course.
    // S'applique à la musique (si activée) et aux effets (même synthétiseur).
    const btnVolumeDown = $('btnVolumeDown');
    const btnVolumeUp = $('btnVolumeUp');
    const refreshVolumeButtons = () => {
      const step = audio.getVolumeStep();
      btnVolumeDown.disabled = step <= 0;
      btnVolumeUp.disabled = step >= MAX_VOLUME_STEP;
    };
    // Retourne true quand le bout de la course est atteint, pour que l'appui long s'arrête net.
    const stepVolume = (delta) => () => {
      const next = Math.max(0, Math.min(MAX_VOLUME_STEP, audio.getVolumeStep() + delta));
      audio.setVolumeStep(next);
      saveLocal(STORAGE_KEYS.volume, next);
      refreshVolumeButtons();
      return next === 0 || next === MAX_VOLUME_STEP;
    };
    bindHoldRepeat(btnVolumeDown, stepVolume(-1));
    bindHoldRepeat(btnVolumeUp, stepVolume(1));
    refreshVolumeButtons();

    // Galerie des portraits (persistée en local)
    galleryView.btnOpen.addEventListener('click', () => {
      pause();
      refreshGallery();
      galleryView.open();
    });
    galleryView.btnClose.addEventListener('click', () => {
      galleryView.close();
      resume();
    });

    // L'appli passe en arrière-plan (et peut être fermée par le système) : le meilleur score garde la partie en cours
    document.addEventListener('visibilitychange', () => { if (document.hidden) saveBestScore(); });
    window.addEventListener('pagehide', saveBestScore);

    bindChrome();
  }

  // --- Compte, en-tête, fenêtres (socle UX) ----------------------------------
  let settingsDialog; let helpDialog; let chargeInfoDialog; let shareModal; let finaleDialog; let aboutDialog;



  const settingsSnapshot = () => ({
    language: i18n.getLanguage(),
    reducedMotion: s.devSettings.reducedMotion,
    ios: s.devSettings.iosVersion,
    settings: s.devSettings,
    batteryPercent: s.filmShots > 0 ? (100 * s.filmLeft) / s.filmShots : 100,
  });

  /**
   * Ouvre une fenêtre en suspendant le jeu ; la reprise a lieu à sa fermeture. Les ouvertures s'imbriquent (« À propos » ouvert par-dessus les réglages) : le jeu
   * ne reprend que quand la dernière fenêtre se ferme.
   */
  let pausedDepth = 0;
  async function paused(open) {
    if (pausedDepth === 0) pause();
    pausedDepth += 1;
    try { return await open(); } finally {
      pausedDepth -= 1;
      if (pausedDepth === 0) resume();
    }
  }

  const saveSettings = () => saveLocal(STORAGE_KEYS.settings, cleanSettings(s.devSettings));

  const applyReducedMotion = (on) => {
    if (on) document.documentElement.setAttribute('data-reduced-motion', '');
    else document.documentElement.removeAttribute('data-reduced-motion');
  };

  // Réglages de composition du niveau : repris à la fermeture des réglages (un niveau qui repart à chaque cran de curseur
  // serait injouable pendant qu'on règle)
  const LEVEL_KEYS = new Set(['worldGrowth', 'photoCostPercent', 'ghostSpeed', 'startLevel', 'familySize']);
  let levelDirty = false;

  // Fenêtre des réglages : chaque changement s'applique en aperçu (musique, affichage, langue, lieu de démo…) mais n'est gardé que par
  // « Enregistrer » ; « Annuler », la croix, Échap ou un clic à côté remettent tout comme à l'ouverture.
  let staging = false;
  let settingsSaved = false;
  let pendingActions = { replayTips: false, resetGame: false }; // cochées dans le formulaire, exécutées à « Enregistrer »
  let settingsBefore = null;
  const settingsState = () => JSON.stringify({ settings: cleanSettings(s.devSettings), language: i18n.getLanguage() });
  const refreshDirty = () => {
    if (staging) settingsDialog.setDirty(settingsState() !== settingsBefore.state || pendingActions.replayTips || pendingActions.resetGame);
  };

  /** Applique tout de suite un curseur de réglage, en aperçu : il n'est mémorisé qu'à « Enregistrer ». */
  function applySetting(settings, batteryLevel, key) {
    s.devSettings = { ...s.devSettings, ...settings };
    audio.setBpm(s.devSettings.musicBpm);
    if (!staging) saveSettings();
    if (LEVEL_KEYS.has(key)) levelDirty = true;
    if (key === 'batteryLevel') applyBatteryLevel(batteryLevel);
    refreshDirty();
  }

  /** « Enregistrer » : tout ce qui a changé est mémorisé sur cet appareil. */
  function saveSettingsDraft() {
    settingsSaved = true;
    saveSettings();
    saveLocal(STORAGE_KEYS.language, i18n.getLanguage());
    toast.show(i18n.t('settings.saved'));
  }

  /** Fermeture sans enregistrer : tout est remis comme à l'ouverture. */
  function restoreSettings() {
    const before = settingsBefore;
    s.devSettings = before.devSettings;
    audio.setBpm(s.devSettings.musicBpm);
    applyReducedMotion(s.devSettings.reducedMotion);
    if (s.filmLeft !== before.filmLeft) { s.filmLeft = before.filmLeft; refreshStats(); }
    levelDirty = false;
    if (i18n.getLanguage() !== before.language) i18n.setLanguage(before.language);
    refreshLanguage(); // redessine la langue et les cases
  }

  async function openSettings() {
    if (settingsDialog.isOpen()) return;
    levelDirty = false;
    settingsSaved = false;
    pendingActions = { replayTips: false, resetGame: false };
    settingsBefore = { devSettings: s.devSettings, language: i18n.getLanguage(), filmLeft: s.filmLeft, state: settingsState() };
    staging = true;
    await paused(() => settingsDialog.open(settingsSnapshot()));
    staging = false;
    if (!settingsSaved) { restoreSettings(); return; }
    if (pendingActions.resetGame) { // « Réinitialiser la partie » cochée : tout est effacé (les réglages enregistrés ci-dessus sont gardés)
      account.reset();
      reloadFresh();
      return;
    }
    // Enregistré : ce qui touche la composition du niveau (taille de la famille, vitesse des kamis…) est repris tout de suite
    if (levelDirty && !isCharging()) {
      levelDirty = false;
      if (s.currentLevel !== s.devSettings.startLevel) resetGallery(); // saut de niveau : nouvelle partie
      s.currentLevel = s.devSettings.startLevel;
      initLevel();
    }
    if (pendingActions.replayTips) await paused(() => helpDialog.open({ dismissible: true })); // « Revoir les astuces » cochée
  }

  /** Redessine tout ce qui dépend de la langue (appelée aussi au démarrage). */
  function refreshLanguage() {
    applyTranslations(i18n.getDict());
    settingsDialog.sync(settingsSnapshot());
    helpDialog.refresh();
    refreshStats();
    refreshScore(); // les milliers se groupent selon la langue
    refreshGallery();
    refreshScoreLine();
    // Si l'écran de fin de niveau (ou l'accueil) est visible, le redessiner dans la nouvelle langue
    if (s.lastOverlay && !$('gameOverlay').hidden) {
      if (s.lastOverlay.situation === 'finale') overlay.showFinale();
      else if (s.lastOverlay.situation === 'charging') showCharging(s.lastOverlay.action);
      else overlay.display(s.lastOverlay.situation, s.lastOverlay.action, s.lastOverlay.messageKey);
    }
  }

  const reloadFresh = () => window.location.reload();

  /** Le jeu peut commencer : l'écran « Jouer » (ou la charge en cours, si la batterie était vide au dernier départ). */
  function enterGame() {
    if (!s.isFirstLoad) return;
    if (s.gameWon) { // page rechargée après la fin du jeu : le bouton « Rejouer », la galerie est toujours là
      s.lastOverlay = { situation: 'finale', action: 'replay' };
      overlay.showFinale();
      refreshScoreLine(); // la partie n'est plus connue : le record
    } else if (isCharging()) showCharging('start'); // rechargé en pleine charge : on attend la fin avant de jouer
    else showOverlay('start', 'start');
  }

  function bindDialogs() {
    helpDialog = createHelpDialog({ t: i18n.t });
    chargeInfoDialog = createChargeInfoDialog({ t: i18n.t, installPromo: !isNativeApp() }); // dans l'appli native, elle est déjà installée
    aboutDialog = createAboutDialog({ getLanguage: i18n.getLanguage });
    finaleDialog = createFinaleDialog({ t: i18n.t });
  }

  function bindChrome() {
    shareModal = createShareModal(undefined, {
      t: i18n.t,
      getLanguage: i18n.getLanguage,
      getGallery: () => s.gallery,
      // La carte d'un kami vient d'être partagée : un nouveau kami donne une charge rapide, tous les kamis de la galerie (au moins deux)
      // une batterie longue durée
      onShared: (card) => {
        const { rewards, fastGained, longLifeGained } = recordShare(s.rewards, card.index, FAMILY_MEMBERS.length, [...knownTypes(s.gallery)]);
        if (fastGained || longLifeGained) saveRewards(rewards);
        return { fastGained, longLifeGained };
      },
    });
    // Lien « À propos » du pied de l'appli : partout, le jeu s'arrête pendant que la fenêtre est ouverte
    $('btnAbout').addEventListener('click', () => { if (!aboutDialog.isOpen()) paused(() => aboutDialog.open()); });
    // Bouton Partager de l'en-tête : la carte à collectionner (le jeu s'arrête pendant que la fenêtre est ouverte)
    $('btnShare').addEventListener('click', () => paused(() => shareModal.open()));

    settingsDialog = createSettingsDialog({
      t: i18n.t,
      game: devView,
      handlers: {
        onLang: (lang) => {
          i18n.setLanguage(lang);
          refreshLanguage();
          refreshDirty();
        },
        onReducedMotion: (on) => {
          s.devSettings = { ...s.devSettings, reducedMotion: on };
          applyReducedMotion(on);
          refreshDirty();
        },
        // Version iOS (simulation) : une charge rapide offerte à chaque niveau ; l'écran de charge se met à jour tout seul
        onIos: (on) => {
          s.devSettings = { ...s.devSettings, iosVersion: on };
          refreshDirty();
        },
        // Les deux actions du formulaire (revoir les astuces, réinitialiser la partie) ne partent qu'à « Enregistrer » : on retient ce qui est coché
        onActions: (actions) => { pendingActions = actions; refreshDirty(); },
        // Remet tous les réglages de jeu (et l'affichage réduit) aux valeurs par défaut ; la langue ne bouge pas
        onResetSettings: () => {
          s.devSettings = cleanSettings(null);
          audio.setBpm(s.devSettings.musicBpm);
          applyReducedMotion(s.devSettings.reducedMotion);
          levelDirty = true; // taille de famille, vitesse des kamis… : repris à l'enregistrement
          const snapshot = settingsSnapshot();
          settingsDialog.sync(snapshot); // resynchronise les cases et les curseurs
          devView.populate(snapshot.settings, snapshot.batteryPercent);
          toast.show(i18n.t('settings.resetDone'));
          refreshDirty();
        },
        onSave: saveSettingsDraft,
      },
    });

    // Réglages cachés : double clic (ou double appui) sur « 0cog™ » dans les mentions légales, sans rien d'apparent (voir
    // ui/settingsSecret.js). Sur le site seulement : les applis des boutiques n'ont pas de fonction cachée (App Store, règle 2.3.1)
    // et jouent avec les réglages par défaut, comme celles de 0ghost Reloaded.
    if (!isNativeApp()) bindSettingsSecret({
      canOpen: () => !settingsDialog.isOpen() && !helpDialog.isOpen(),
      onOpen: async () => {
        if (aboutDialog.isOpen()) {
          // « À propos » se referme d'abord (et la partie reprend) : les réglages, qui la mettent en pause, ne s'ouvrent qu'après
          aboutDialog.close();
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        openSettings();
      },
    });

    applyReducedMotion(s.devSettings.reducedMotion);
  }

  // --- Démarrage ------------------------------------------------------------
  async function start() {
    applyTranslations(i18n.getDict()); // avant tout affichage, pour éviter un flash dans la mauvaise langue
    bindUI();

    // Téléphone hors version installée : en mode panoramique (paysage), fenêtre bloquante « installe l'application pour jouer en mode
    // panoramique ». En portrait, rien n'est exigé ; la fenêtre apparaît à la rotation (le jeu se met en pause) et disparaît au retour
    // en portrait. Au lancement en paysage, ni astuces ni jeu ne démarrent tant qu'on n'est pas repassé en portrait. ?install=off la
    // désactive, ?install=gate la force (même hors téléphone, avec ?layout=landscape ou une fenêtre large).
    let splashDone = false;
    const installOverride = new URLSearchParams(window.location.search).get('install');
    const installRequired = needsInstallGate({
      phone: detectPhone(),
      standalone: installOffer.isStandalone(),
      override: installOverride,
      dev: import.meta.env.DEV,
    });
    // L'astuce « Tourne ton téléphone » ne parle d'installer l'appli que là où la fenêtre d'installation apparaîtrait
    $('helpRotateInstall').hidden = !installRequired;
    if (installRequired) {
      const diagnostics = installOverride === 'debug' ? JSON.stringify(measureDevice()) : null;
      const gate = createInstallGate({ installOffer, t: i18n.t, diagnostics });
      let wasPaused = false;
      const guard = guardLandscape(gate, {
        onBlock: () => { wasPaused = s.isPaused; pause(); },
        onUnblock: () => { if (!wasPaused) resume(); },
      });
      if (guard.isBlocking()) {
        await dismissSplash({ ready: document.fonts?.ready ?? Promise.resolve() });
        splashDone = true;
        await guard.whenFree();
      }
    }

    // Installation : seule occasion offerte, une fois au lancement (jamais si déjà installé, ni
    // si déjà proposée il y a moins de INSTALL_OFFER_INTERVAL_DAYS) ; ensuite, plus aucun moyen
    // d'installer depuis le jeu sans recharger la page.
    offerInstallOnce(installOffer, {
      due: installOfferDue,
      onPresented: () => saveLocal(STORAGE_KEYS.installOfferedAt, Date.now()),
    });

    refreshGallery();
    initLevel();

    // Premier lancement seulement : les astuces (qu'on ne quitte qu'après la dernière), puis on joue ; elles se revoient dans les réglages.
    // Elles s'ouvrent derrière l'écran d'accueil, avant son effacement, pour qu'on ne voie jamais le jeu entre les deux.
    const tips = loadLocal(STORAGE_KEYS.tipsSeen, false)
      ? null
      : helpDialog.open({ dismissible: false }).then(() => saveLocal(STORAGE_KEYS.tipsSeen, true));

    // L'écran d'accueil s'efface en fondu une fois le délai minimal passé et les polices prêtes
    if (!splashDone) await dismissSplash({ ready: document.fonts?.ready ?? Promise.resolve() });

    await tips;
    enterGame();
  }

  return { start, get state() { return s; } };
}
